import { FactoryDepartment } from '../enums/factory-department.enum';
import { FactoryMoveRequestStatus } from '../enums/factory-move-request-status.enum';
import { PrismModule } from '../enums/prism-module.enum';
import { UploadedFile } from './uploaded-file.model';

export type FactoryMoveRequestLinkedItemType = 'product' | 'hardware-item' | 'order-line';

/**
 * Order-line facts frozen when the move request is created. The live ids make
 * the relationship navigable; the snapshots keep old requests understandable
 * if an order or product is edited later.
 */
export interface FactoryMoveRequestOrderLineDetails {
    orderId: string;
    orderLineId: string;
    orderNumber: string;
    customerId: string;
    customerName: string;
    customerPurchaseOrderNumber: string;
    salesOrderNumber: string;
    customerPartNumber: string;
    dueDate: string;
    status: string;
    orderedQuantity: number;
    completedQuantityAtRequest: number;
    moveQuantity: number;
}

/** Optional catalog snapshot embedded directly in a simple Move Request. */
export interface FactoryMoveRequestLinkedItem {
    id: string;
    sourceModule: PrismModule;
    entityType: FactoryMoveRequestLinkedItemType;
    entityId: string;
    entityVersionId: string;
    partNumber: string;
    description: string;
    thumbnailImageUrl: string;
    units: string;
    route: string;
    /** Optional because ordinary moves do not require a structured quantity. */
    moveQuantity?: number | null;
    note?: string;
    orderLine?: FactoryMoveRequestOrderLineDetails;
}

export function isFactoryMoveRequestOrderLine(
    item: FactoryMoveRequestLinkedItem,
): item is FactoryMoveRequestLinkedItem & { orderLine: FactoryMoveRequestOrderLineDetails } {
    return item.entityType === 'order-line' && !!item.orderLine;
}

/** Query-only reverse index; linkedItems remains the authoritative snapshot. */
export function factoryMoveRequestOrderLineIds(
    linkedItems: FactoryMoveRequestLinkedItem[] | null | undefined,
): string[] {
    return [...new Set((linkedItems ?? [])
        .filter(isFactoryMoveRequestOrderLine)
        .map((item) => item.orderLine.orderLineId || item.entityId)
        .filter(Boolean))];
}

export interface FactoryMoveRequest {
    id: string;
    title: string;
    description: string;
    fromDepartment: FactoryDepartment;
    fromLocation: string;
    toDepartment: FactoryDepartment;
    toLocation: string;
    deadline: Date;
    handlingNotes: string;
    files: UploadedFile[];
    linkedItems: FactoryMoveRequestLinkedItem[];
    /** Allows an order line to find its related move requests without a full-table download. */
    linkedOrderLineIds: string[];
    /** Additional people who should hear when this move is completed. */
    followerIds: string[];
    status: FactoryMoveRequestStatus;
    createdOn: Date;
    createdById: string;
    updatedOn: Date;
    updatedById: string;
    completedOn: Date | null;
    completedById: string;
    canceledOn: Date | null;
    canceledById: string;
}

export interface FactoryMoveRequestOrderLineReference {
    request: FactoryMoveRequest;
    linkedItem: FactoryMoveRequestLinkedItem & { orderLine: FactoryMoveRequestOrderLineDetails };
}

export function factoryMoveRequestReferencesByOrderLineId(
    requests: FactoryMoveRequest[] | null | undefined,
): Record<string, FactoryMoveRequestOrderLineReference[]> {
    const references: Record<string, FactoryMoveRequestOrderLineReference[]> = {};
    for (const request of requests ?? []) {
        const seen = new Set<string>();
        for (const linkedItem of request.linkedItems ?? []) {
            if (!isFactoryMoveRequestOrderLine(linkedItem)) continue;
            const orderLineId = linkedItem.orderLine.orderLineId || linkedItem.entityId;
            if (!orderLineId || seen.has(orderLineId)) continue;
            seen.add(orderLineId);
            (references[orderLineId] ??= []).push({ request, linkedItem });
        }
    }
    for (const lineReferences of Object.values(references)) {
        lineReferences.sort((left, right) => (
            (right.request.completedOn ?? right.request.canceledOn ?? right.request.createdOn).getTime()
            - (left.request.completedOn ?? left.request.canceledOn ?? left.request.createdOn).getTime()
        ));
    }
    return references;
}

export type FactoryMoveRequestEditableUpdate = Pick<
    FactoryMoveRequest,
    | 'title'
    | 'description'
    | 'fromDepartment'
    | 'fromLocation'
    | 'toDepartment'
    | 'toLocation'
    | 'deadline'
    | 'handlingNotes'
    | 'files'
    | 'linkedItems'
    | 'linkedOrderLineIds'
    | 'followerIds'
    | 'updatedOn'
    | 'updatedById'
>;

/**
 * Produces the only fields a normal edit may write. In particular this never
 * includes status or lifecycle fields, so a stale editor cannot reopen work.
 */
export function toFactoryMoveRequestEditableUpdate(request: FactoryMoveRequest): FactoryMoveRequestEditableUpdate {
    return {
        title: request.title,
        description: request.description,
        fromDepartment: request.fromDepartment,
        fromLocation: request.fromLocation,
        toDepartment: request.toDepartment,
        toLocation: request.toLocation,
        deadline: request.deadline,
        handlingNotes: request.handlingNotes,
        files: request.files,
        linkedItems: request.linkedItems,
        linkedOrderLineIds: factoryMoveRequestOrderLineIds(request.linkedItems),
        followerIds: [...new Set(request.followerIds ?? [])],
        updatedOn: request.updatedOn,
        updatedById: request.updatedById,
    };
}

/** Creator and followers receive completion news once each; the acting user does not notify themselves. */
export function factoryMoveRequestCompletionRecipientIds(
    request: Pick<FactoryMoveRequest, 'createdById' | 'followerIds'>,
    actorId: string,
): string[] {
    return [...new Set([request.createdById, ...(request.followerIds ?? [])])]
        .filter(id => !!id && id !== actorId);
}

/** Applies the intentionally small Open -> Completed/Canceled state machine. */
export function applyFactoryMoveRequestTransition(
    request: FactoryMoveRequest,
    status: FactoryMoveRequestStatus.Completed | FactoryMoveRequestStatus.Canceled,
    actorId: string,
    transitionOn = new Date(),
): FactoryMoveRequest {
    if (request.status !== FactoryMoveRequestStatus.Open) {
        throw new Error(`Move request is already ${request.status.toLowerCase()}.`);
    }

    return {
        ...request,
        status,
        updatedOn: transitionOn,
        updatedById: actorId,
        completedOn: status === FactoryMoveRequestStatus.Completed ? transitionOn : null,
        completedById: status === FactoryMoveRequestStatus.Completed ? actorId : '',
        canceledOn: status === FactoryMoveRequestStatus.Canceled ? transitionOn : null,
        canceledById: status === FactoryMoveRequestStatus.Canceled ? actorId : '',
    };
}

/** ASAP is represented only by a deadline set at submission/edit time. */
export function isFactoryMoveRequestAsap(
    request: Pick<FactoryMoveRequest, 'createdOn' | 'deadline'> & Partial<Pick<FactoryMoveRequest, 'updatedOn'>>,
    toleranceMs = 60_000,
): boolean {
    const deadline = new Date(request.deadline).getTime();
    return [request.createdOn, request.updatedOn]
        .filter((value): value is Date => !!value)
        .some(value => Math.abs(deadline - new Date(value).getTime()) <= toleranceMs);
}

/** Open work is ordered by deadline, with creation time as a stable tie-breaker. */
export function compareOpenFactoryMoveRequests(
    left: Pick<FactoryMoveRequest, 'deadline' | 'createdOn'>,
    right: Pick<FactoryMoveRequest, 'deadline' | 'createdOn'>,
): number {
    const deadlineDifference = new Date(left.deadline).getTime() - new Date(right.deadline).getTime();
    if (deadlineDifference !== 0) return deadlineDifference;
    return new Date(left.createdOn).getTime() - new Date(right.createdOn).getTime();
}
